import { DateTimeUtil } from '../utils/dateTimeUtil';
import { computed, ref, watch } from 'vue';

/**
 * Composable for managing vacation date selection logic.
 * Handles reactive state, picker constraints, autocorrection, and validation rules.
 *
 * @param initialStartDate - Initial value for start date (YYYY-MM-DD or null).
 * @param initialEndDate - Initial value for end date (YYYY-MM-DD or null).
 * @param initialDeadline - Initial value for response deadline (YYYY-MM-DD or null).
 */
export const useVacationDatePicker = (initialStartDate = null, initialEndDate = null, initialDeadline = null, blockToday = false) => {
  const MAX_YEAR_VACATION_DURATION = 1;
  const FIRST_DAY_OF_WEEK = 1;

  // State
  const startDate = ref<string | null>(initialStartDate);
  const endDate = ref<string | null>(initialEndDate);
  const deadline = ref<string | null>(initialDeadline);

  /**
   * The lower bound for the vacation period.
   * Returns the selected Start Date, or Today if no start date is selected.
   */
  const startLimit = computed(() => {
    const today = new Date();
    if (startDate.value) {
      return new Date(startDate.value);
    }
    return today;
  });

  /**
   * The upper bound for the vacation period.
   * Defined as 1 year from the Start Date (or Today if not selected).
   */
  const endLimit = computed(() => {
    const baseDate = startDate.value || new Date();
    return new Date(DateTimeUtil.addTime(baseDate, MAX_YEAR_VACATION_DURATION, 'year'));
  });

  /**
   * Configuration for Start Date Picker.
   * Rule: Block past dates. If blockToday is true, also block today.
   */
  const startDatePickerOptions = computed(() => ({
    firstDayOfWeek: FIRST_DAY_OF_WEEK,
    disabledDate: (time: Date) =>
      blockToday ? DateTimeUtil.isSameOrBefore(time, new Date()) : DateTimeUtil.isBefore(time, new Date()),
  }));

  const isDisabledDeadline = computed(() => {
    if (!startDate.value) {
      return false;
    }

    return DateTimeUtil.isSameOrBefore(startDate.value, new Date());
  });

  /**
   * Configuration for End Date Picker.
   * Rule: Block past dates. If Start Date is selected, block dates before Start Limit
   * or after End Limit (1 year max duration).
   */
  const endDatePickerOptions = computed(() => ({
    firstDayOfWeek: FIRST_DAY_OF_WEEK,
    disabledDate: (time: Date) => {
      const today = new Date();
      if (DateTimeUtil.isBefore(time, today)) {
        return true;
      }

      if (startDate.value) {
        if (DateTimeUtil.isBefore(time, startLimit.value)) {
          return true;
        }
        if (DateTimeUtil.isAfter(time, endLimit.value)) {
          return true;
        }
      }
      return false;
    },
  }));

  /**
   * Configuration for Deadline Picker.
   * Rule: Block past dates and dates occurring on or after the Start Date.
   */
  const deadlinePickerOptions = computed(() => ({
    firstDayOfWeek: FIRST_DAY_OF_WEEK,
    disabledDate: (time: Date) => {
      const today = new Date();
      if (DateTimeUtil.isBefore(time, today)) {
        return true;
      }

      if (startDate.value && DateTimeUtil.isSameOrAfter(time, startDate.value)) {
        return true;
      }
      return false;
    },
  }));

  /**
   * Watcher to maintain data integrity when Start Date changes.
   * 1. Resets Deadline if it becomes invalid (overlaps with new Start Date).
   * 2. Resets End Date if it falls outside the new valid window [Start Date, Start Date + 1 Year].
   */
  watch(startDate, newStart => {
    if (!newStart) {
      return;
    }

    // Reset deadline if invalid (must be strictly before start date)
    if (deadline.value) {
      if (DateTimeUtil.isSameOrAfter(deadline.value, newStart)) {
        deadline.value = null;
      }
    }

    if (!endDate.value) {
      return;
    }

    const isBeforeStart = DateTimeUtil.isBefore(endDate.value, newStart);
    const isAfterLimit = DateTimeUtil.isAfter(endDate.value, endLimit.value);

    if (isBeforeStart || isAfterLimit) {
      endDate.value = null;
    }
  });

  /**
   * Checks if the selected date range contains at least one working day.
   * Delegates logic to DateTimeUtil.
   */
  const hasWorkingDay = (): boolean => DateTimeUtil.hasWorkingDay(startDate.value, endDate.value);

  /**
   * Validates if the End Date is chronologically correct and within the allowed duration.
   * @returns true if End Date is valid (after Start Date and within 1 year limit).
   */
  const isEndDateValid = (): boolean => {
    if (!startDate.value || !endDate.value) {
      return true;
    }

    if (DateTimeUtil.isBefore(endDate.value, startDate.value)) {
      return false;
    }

    if (DateTimeUtil.isAfter(endDate.value, endLimit.value)) {
      return false;
    }

    return true;
  };

  return {
    startDate,
    endDate,
    deadline,
    startDatePickerOptions,
    endDatePickerOptions,
    deadlinePickerOptions,
    isDisabledDeadline,
    hasWorkingDay,
    isEndDateValid,
  };
};
